import React from "react";
import { NavLink, Outlet } from "react-router-dom";
import { BadgeCheck, Banknote, Boxes, Building2, Cable, CalendarRange, ChevronLeft, Clock, Coins, FileDown, FileUp, History, ListTree, Map, Mail, Package, Percent, Receipt, Settings2, ShieldCheck, Star, Tags, TrendingUp, Truck, Users, Wallet, Wrench } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";

const ITEMS = [
  { to: "/admin/camping", key: "admin_camping", icon: Settings2, desc: "admin_camping_desc" },
  { to: "/admin/gebruikers", key: "users", icon: Users, desc: "admin_users_desc" },
  { to: "/admin/plattegrond", key: "admin_map", icon: Map, desc: "admin_map_desc" },
  { to: "/admin/controle", key: "admin_inspection", icon: ShieldCheck, desc: "admin_inspection_desc" },
  { to: "/admin/voorraad", key: "admin_stock", icon: Boxes, desc: "admin_stock_desc" },
  { to: "/admin/tarieven", key: "admin_tariffs", icon: Tags, desc: "admin_tariffs_desc" },
  { to: "/admin/omzet", key: "admin_revenue", icon: TrendingUp, desc: "admin_revenue_desc" },
  { to: "/admin/omzet-medewerker", key: "admin_seller_revenue", icon: BadgeCheck, desc: "seller_revenue_desc" },
  { to: "/admin/verhuur", key: "admin_rentals", icon: Package, desc: "admin_rentals_desc" },
  { to: "/admin/leveranciers", key: "admin_suppliers", icon: Truck, desc: "admin_suppliers_desc" },
  { to: "/admin/inkomende-facturen", key: "admin_purchases", icon: FileDown, desc: "admin_purchases_desc" },
  { to: "/admin/kosten", key: "admin_expenses", icon: Receipt, desc: "admin_expenses_desc", fin4: true },
  { to: "/admin/uitgaande-facturen", key: "admin_sales_inv", icon: FileUp, desc: "admin_sales_desc" },
  { to: "/admin/dagafsluiting", key: "admin_cashclose", icon: Coins, desc: "admin_cashclose_desc" },
  { to: "/admin/voorschotten", key: "admin_prepayments", icon: Wallet, desc: "admin_prepayments_desc", fin4: true },
  { to: "/admin/btw-overzicht", key: "admin_vat", icon: Percent, desc: "admin_vat_desc", fin4: true },
  { to: "/admin/onderhoud", key: "admin_maintenance", icon: Wrench, desc: "admin_maintenance_desc" },
  { to: "/admin/menu", key: "admin_menu", icon: ListTree, desc: "admin_menu_desc", super: true },
  { to: "/admin/gastenboek", key: "admin_guestbook", icon: Star, desc: "admin_guestbook_desc" },
  { to: "/admin/mailserver", key: "admin_email", icon: Mail, desc: "admin_email_desc" },
  { to: "/admin/interfaces", key: "admin_interfaces", icon: Cable, desc: "admin_interfaces_desc", open: true },
  { to: "/admin/aanmeldlog", key: "admin_loginlog", icon: ShieldCheck, desc: "admin_loginlog_desc" },
  { to: "/admin/logboek", key: "admin_audit", icon: History, desc: "admin_audit_desc" },
  { to: "/admin/uren", key: "admin_hours", icon: Clock, desc: "hours_desc" },
  { to: "/admin/afstortingen", key: "admin_deposits", icon: Banknote, desc: "deposits_desc", fin4: true },
  { to: "/admin/jaaroverzicht", key: "admin_season", icon: CalendarRange, desc: "season_desc", fin5: true },
  { to: "/admin/campings", key: "tenants", icon: Building2, desc: "tenants_desc", super: true },
];

export default function AdminLayout() {
  const { t } = useLang();
  const { user } = useAuth();
  const isAdmin = ["admin", "superadmin"].includes(user?.role);
  const items = ITEMS.filter((i) => (isAdmin || i.open) && (!i.super || user?.is_super) && (!i.fin5 || Number(user?.finance_class) === 5) && (!i.fin4 || Number(user?.finance_class) >= 4));
  const [collapsed, setCollapsed] = React.useState(() => localStorage.getItem("cb_admin_nav") === "1");
  const toggle = () => setCollapsed((v) => { localStorage.setItem("cb_admin_nav", v ? "0" : "1"); return !v; });

  return (
    <div className={`grid grid-cols-1 gap-6 items-start ${collapsed ? "lg:grid-cols-[4.25rem_minmax(0,1fr)]" : "lg:grid-cols-[15rem_minmax(0,1fr)]"}`}>
      <nav className="rounded-xl border border-border bg-card shadow-sm p-2 lg:sticky lg:top-24 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto overscroll-contain cb-scroll transition-all" data-testid="admin-subnav">
        {!collapsed && <p className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{t("nav_admin")}</p>}
        {items.map(({ to, key, icon: Icon }) => (
          <NavLink key={to} to={to} data-testid={`admin-nav-${key}`} title={t(key)}
            className={({ isActive }) => `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              collapsed ? "justify-center" : ""} ${isActive ? "bg-secondary text-primary font-semibold" : "text-muted-foreground hover:bg-muted"}`}>
            <Icon className="h-4 w-4 shrink-0" />{!collapsed && t(key)}
          </NavLink>
        ))}
        <button onClick={toggle} data-testid="admin-nav-toggle"
          className={`mt-1 w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-xs text-muted-foreground hover:bg-muted transition-colors ${collapsed ? "justify-center" : ""}`}>
          <ChevronLeft className={`h-4 w-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
          {!collapsed && t("collapse")}
        </button>
      </nav>
      <div className="space-y-6 min-w-0">
        <Outlet />
      </div>
    </div>
  );
}
